import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Users, FileText, Video, ArrowRight, CheckCircle, Clock, Target, Mail, Bell } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // TODO: Add actual waitlist API endpoint
    setTimeout(() => {
      toast.success('Thanks for joining the waitlist! We\'ll notify you when GoJobs launches.');
      setEmail('');
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-pink-900 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0V0zm40 40h40v40H40V40z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Coming Soon Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-semibold mb-6 border border-white/30">
              <Clock className="w-4 h-4" />
              <span>Coming Soon</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              Find Your Dream Job or Hire Top Talent
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-purple-100 max-w-3xl mx-auto drop-shadow">
              Australia's next-generation job marketplace with video cover letters and one-way interviews
            </p>
            
            {/* Waitlist Form */}
            <div className="max-w-md mx-auto mb-8">
              <form onSubmit={handleWaitlist} className="flex gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-4 rounded-xl text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-all shadow-xl disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? 'Joining...' : 'Join Waitlist'}
                </button>
              </form>
              <p className="text-purple-200 text-sm mt-3">
                Be the first to know when we launch!
              </p>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Target className="w-5 h-5" />
                <span className="font-medium">Smart Job Matching</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Video className="w-5 h-5" />
                <span className="font-medium">Video Applications</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">One-Way Interviews</span>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" className="fill-background"/>
          </svg>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">What Makes GoJobs Different?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're reinventing the job search and hiring experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Video className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Video Cover Letters</h3>
              <p className="text-muted-foreground">
                Stand out with video cover letters. Show your personality and passion beyond your resume.
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">One-Way Interviews</h3>
              <p className="text-muted-foreground">
                Employers can send interview questions. Record answers on your time, no scheduling needed.
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-pink-300 dark:hover:border-pink-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Smart Matching</h3>
              <p className="text-muted-foreground">
                AI-powered job matching connects you with opportunities that truly fit your skills and goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Job Seekers & Employers */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">For Job Seekers & Employers</h2>
              <p className="text-xl text-muted-foreground">Two powerful platforms in one</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Job Seekers */}
              <div className="bg-card rounded-2xl p-8 shadow-xl border-2 border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">For Job Seekers</h3>
                </div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Create professional profile with resume</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Upload video cover letters to stand out</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Apply to jobs with one click</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Track applications in real-time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Participate in video interviews on your schedule</span>
                  </li>
                </ul>
                
                <button
                  onClick={() => user ? navigate('/jobs/onboarding?type=job_seeker') : navigate('/jobs/register')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
                >
                  I'm Looking for a Job
                </button>
              </div>
              
              {/* Employers */}
              <div className="bg-card rounded-2xl p-8 shadow-xl border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">For Employers</h3>
                </div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Post unlimited job listings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Access qualified candidates instantly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Review video applications and interviews</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Manage hiring pipeline efficiently</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Build your employer brand</span>
                  </li>
                </ul>
                
                <button
                  onClick={() => user ? navigate('/jobs/onboarding?type=employer') : navigate('/jobs/register')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
                >
                  I Want to Hire
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Features */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Launching Soon</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're building something special. Here's what to expect.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-card rounded-xl p-6 border-2 border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mb-4">
                <Briefcase className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-bold mb-2 text-foreground">Job Listings</h3>
              <p className="text-sm text-muted-foreground">
                Thousands of opportunities across all industries and locations
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-bold mb-2 text-foreground">Video Applications</h3>
              <p className="text-sm text-muted-foreground">
                Record video cover letters to showcase your personality
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 border-2 border-border hover:border-pink-300 dark:hover:border-pink-600 transition-colors">
              <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="font-bold mb-2 text-foreground">One-Way Interviews</h3>
              <p className="text-sm text-muted-foreground">
                Complete interviews on your schedule, no back-and-forth
              </p>
            </div>

            <div className="bg-card rounded-xl p-6 border-2 border-border hover:border-green-300 dark:hover:border-green-600 transition-colors">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-bold mb-2 text-foreground">Smart Matching</h3>
              <p className="text-sm text-muted-foreground">
                AI-powered recommendations for perfect job-candidate fits
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">Development Timeline</h2>
              <p className="text-xl text-muted-foreground">
                We're working hard to bring you the best experience
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4 items-start bg-card p-6 rounded-xl border border-border">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Phase 1: Foundation (Complete)</h3>
                  <p className="text-muted-foreground">Platform architecture, user accounts, and data models established</p>
                </div>
              </div>

              <div className="flex gap-4 items-start bg-card p-6 rounded-xl border-2 border-blue-300 dark:border-blue-600">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Phase 2: Core Features (In Progress)</h3>
                  <p className="text-muted-foreground">Job listings, applications, employer dashboard, and job seeker profiles</p>
                </div>
              </div>

              <div className="flex gap-4 items-start bg-card p-6 rounded-xl border border-border">
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-muted-foreground font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1 text-foreground">Phase 3: Video Features (Coming)</h3>
                  <p className="text-muted-foreground">Video cover letters, one-way interviews, and advanced matching algorithms</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-pink-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Bell className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">
              Be Among the First
            </h2>
            <p className="text-xl text-purple-100 mb-8 drop-shadow">
              Join the waitlist and get exclusive early access when we launch
            </p>
            
            <form onSubmit={handleWaitlist} className="max-w-md mx-auto flex gap-3 mb-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-all shadow-xl disabled:opacity-50 whitespace-nowrap"
              >
                {loading ? 'Joining...' : 'Notify Me'}
              </button>
            </form>

            <p className="text-purple-200 text-sm">
              In the meantime, check out <button onClick={() => navigate('/tradies')} className="underline hover:text-white font-semibold">GoTradies</button> for all your trade service needs!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobsLanding;
